/** Refunds list + grant/send/reject actions (Phase C2 MVP).
 *
 * Поток статусов: pending → granted → sent (или → rejected).
 * Создание refund'ов идёт из order detail (TODO inline в OrderShow). Здесь —
 * staff queue для обработки.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  FileBox,
  Send,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

interface Refund {
  id: number;
  order_id: number;
  status: "pending" | "granted" | "sent" | "rejected";
  amount_tiyin: number;
  reason: string | null;
  line_items: Array<Record<string, unknown>>;
  granted_by: number | null;
  granted_at: string | null;
  sent_by: number | null;
  sent_at: string | null;
  provider_refund_id: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_OPTIONS = ["all", "pending", "granted", "sent", "rejected"] as const;
type StatusFilter = (typeof STATUS_OPTIONS)[number];

const STATUS_COLOR: Record<Refund["status"], string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  granted: "bg-blue-100 text-blue-800 border-blue-300",
  sent: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

function fmtSum(tiyin: number) {
  return `${Math.round(tiyin / 100).toLocaleString("ru-RU")} сум`;
}

export function RefundsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const { data: refunds, isLoading } = useQuery({
    queryKey: ["admin-refunds", statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status_filter", statusFilter);
      const { data } = await api.get(
        `/refunds${params.toString() ? `?${params}` : ""}`,
      );
      return data as Refund[];
    },
  });

  const grantMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/refunds/${id}/grant`);
    },
    onSuccess: () => {
      toast.success("Возврат одобрен");
      queryClient.invalidateQueries({ queryKey: ["admin-refunds"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось"),
  });

  const sendMutation = useMutation({
    mutationFn: async ({ id, providerId }: { id: number; providerId: string }) => {
      await api.post(`/refunds/${id}/send`, {
        provider_refund_id: providerId || null,
      });
    },
    onSuccess: () => {
      toast.success("Возврат отправлен");
      queryClient.invalidateQueries({ queryKey: ["admin-refunds"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось"),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      await api.post(`/refunds/${id}/reject`, { reason: reason || null });
    },
    onSuccess: () => {
      toast.success("Возврат отклонён");
      queryClient.invalidateQueries({ queryKey: ["admin-refunds"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <FileBox className="w-6 h-6" />
            Возвраты
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Двухэтапная обработка: pending → granted → sent. Создание —
            из карточки заказа (TBD).
          </p>
        </div>
        <div className="space-y-1.5 w-48">
          <Label className="text-xs">Статус</Label>
          <Select
            value={statusFilter}
            onValueChange={(v: StatusFilter) => setStatusFilter(v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === "all" ? "Все" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : refunds?.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Возвратов нет.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {refunds?.map((r) => (
            <RefundCard
              key={r.id}
              refund={r}
              onGrant={() => grantMutation.mutate(r.id)}
              onSend={(providerId) =>
                sendMutation.mutate({ id: r.id, providerId })
              }
              onReject={(reason) =>
                rejectMutation.mutate({ id: r.id, reason })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RefundCard({
  refund,
  onGrant,
  onSend,
  onReject,
}: {
  refund: Refund;
  onGrant: () => void;
  onSend: (providerId: string) => void;
  onReject: (reason: string) => void;
}): JSX.Element {
  const [providerId, setProviderId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs">
              #{refund.id}
            </Badge>
            <Badge className={STATUS_COLOR[refund.status]}>{refund.status}</Badge>
            <Link
              to={`/orders/${refund.order_id}`}
              className="text-sm font-medium hover:underline flex items-center gap-1"
            >
              Заказ #{refund.order_id}
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="text-lg font-semibold">{fmtSum(refund.amount_tiyin)}</div>
        </div>

        {refund.reason && (
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {refund.reason}
          </p>
        )}

        <div className="text-xs text-muted-foreground space-x-3">
          <span>Создан: {new Date(refund.created_at).toLocaleString("ru-RU")}</span>
          {refund.granted_at && (
            <span>
              · Одобрен: {new Date(refund.granted_at).toLocaleString("ru-RU")}
            </span>
          )}
          {refund.sent_at && (
            <span>
              · Отправлен: {new Date(refund.sent_at).toLocaleString("ru-RU")}
            </span>
          )}
          {refund.provider_refund_id && (
            <span>· Provider ID: {refund.provider_refund_id}</span>
          )}
        </div>

        {refund.line_items.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Позиции ({refund.line_items.length})
            </summary>
            <pre className="mt-1 p-2 bg-muted/30 rounded font-mono text-[10px] overflow-auto">
              {JSON.stringify(refund.line_items, null, 2)}
            </pre>
          </details>
        )}

        {/* Actions */}
        {refund.status === "pending" && (
          <div className="flex gap-2">
            <Button size="sm" onClick={onGrant}>
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Одобрить
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowReject(!showReject)}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Отклонить
            </Button>
          </div>
        )}

        {refund.status === "granted" && (
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs">
                Provider refund ID (опционально — если уже отправлено вручную)
              </Label>
              <Input
                placeholder="payme-tx-12345"
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
              />
            </div>
            <Button size="sm" onClick={() => onSend(providerId)}>
              <Send className="w-4 h-4 mr-1" />
              Отправить
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowReject(!showReject)}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Отклонить
            </Button>
          </div>
        )}

        {showReject && (
          <div className="space-y-2 border-t pt-2">
            <Label className="text-xs">Причина отказа</Label>
            <Textarea
              rows={2}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Например: «Сроки истекли»"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowReject(false)}
              >
                Отмена
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  onReject(rejectReason);
                  setShowReject(false);
                }}
              >
                Подтвердить отказ
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
